import { persistReducer } from 'redux-persist'
import storage from 'redux-persist/lib/storage'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type {
  BetSlipItem,
  BetErrorType,
  MultiInvestment,
  InvestmentType,
  FobBetReceiptResponse,
  Campaign,
} from '@mobi/betslip/types'
import { isFobSelection, isAllUpSelection } from '@mobi/betslip/helpers/typeGuards'
import {
  confirmAllBets,
  proposeAllBets,
  refreshAllBets,
} from '@mobi/betslip/Store/Workflow/asyncActions'
import {
  areItemsInBetSlip,
  getBetsInMulti,
  getMultiLegEventOrRaceKey,
  hasNoFatalErrors,
  hasTooFewMultiLegs,
  hasTooManyMultiLegs,
  isSpecialUsed,
} from '@mobi/betslip/helpers/state'
import { getItemsWithBonusesApplied } from '@mobi/betslip/Components/Singles/helpers/getItemsWithBonusesApplied'
import { mapResponse } from './helpers/apiResponse/mapItemsWithResponse'
import { updateItemInvestment } from './helpers/investment/updateItem'
import { getMultiBetResponse } from './helpers/apiResponse/multi'
import { getMultiBetErrorDescription } from './helpers/apiResponse/errors'
import { updateItemsWithCampaign } from './helpers/campaigns/updateItemsWithCampaign'
import { resetMultiInvestment } from './helpers/campaigns/resetMultiInvestment'
import { updateItemsWithCampaignAndInvestment } from './helpers/campaigns/updateItemsWithCampaignAndInvestment'

export const defaultBetSlipBetsState: BetSlipBetsState = {
  items: [],
  isSingleExpanded: true,
  isMultiExpanded: true,
  multiBetError: null,
  multiInvestment: {
    value: 0,
    f1: 0,
    f2: 0,
    f3: 0,
    f4: 0,
    f5: 0,
    bonusBetId: null,
    isBonusBet: false,
    isBonusCash: false,
  },
  multiReceipt: undefined,
}

const betsSlice = createSlice({
  name: 'betslip/bets',
  initialState: defaultBetSlipBetsState,
  reducers: {
    addItemsToBetSlip(state, { payload: items }: PayloadAction<BetSlipItem[]>) {
      if (areItemsInBetSlip(state.items, items)) {
        return
      }

      let itemIdsToDeselectIsInMulti: string[] = []
      const hasItemsEligibleForMulti = items.some(item => item.isInMulti)

      if (hasItemsEligibleForMulti) {
        const newMultiItems = items.flatMap(item => (item.isInMulti ? [item] : []))
        const currMultiKey = getBetsInMulti(state.items).map(item =>
          getMultiLegEventOrRaceKey(item.selectionDetails)
        )
        itemIdsToDeselectIsInMulti = newMultiItems.flatMap(item =>
          currMultiKey.includes(getMultiLegEventOrRaceKey(item.selectionDetails)) ? [item.id] : []
        )
      }
      if (itemIdsToDeselectIsInMulti.length > 0) {
        items.forEach(item => {
          if (itemIdsToDeselectIsInMulti.includes(item.id)) {
            item.isInMulti = false
          }
        })
      }

      state.items.push(...items)
    },

    clearAllBetSlipItems(state) {
      state.items = []
      state.multiBetError = null
      state.multiInvestment = { ...defaultBetSlipBetsState.multiInvestment }
    },

    removeItemFromBetSlipById(state, { payload }: PayloadAction<Pick<BetSlipItem, 'id'>>) {
      state.items = state.items.filter(item => {
        if (payload.id) {
          return item.id !== payload.id
        }
        return true
      })

      if (hasTooFewMultiLegs(state.items) || hasTooManyMultiLegs(state.items)) {
        state.multiInvestment = { ...defaultBetSlipBetsState.multiInvestment }
      }
    },

    setInvestmentAmount(state, { payload }: SetInvestmentAmountArgs) {
      const { investmentType, itemId, value } = payload
      updateItemInvestment({ investmentType, value, state, betId: itemId })
    },

    setSelectedSpecialOffer(
      state,
      {
        payload,
      }: PayloadAction<{
        id: BetSlipItem['id']
        specialOffer: BetSlipItem['selectedSuperPickOffer']
        bonusCashCampaigns?: Campaign[]
      }>
    ) {
      const itemToUpdate = state.items.find(item => item.id === payload.id)
      if (!itemToUpdate) return

      const { betSlipItems } = getItemsWithBonusesApplied(
        [...state.items],
        payload.bonusCashCampaigns ?? [],
        state.multiInvestment,
        state.multiReceipt
      )

      // reset campaign
      if (itemToUpdate?.campaign?.id) {
        itemToUpdate.campaign = undefined
        itemToUpdate.investment.win.value = 0
        itemToUpdate.investment.place.value = 0
        itemToUpdate.investment.win.isBonusBet = false
        itemToUpdate.investment.place.isBonusBet = false
        itemToUpdate.investment.win.isBonusCash = false
        itemToUpdate.investment.place.isBonusCash = false
        itemToUpdate.investment.bonusBet = undefined
      }

      state.items = updateItemsWithCampaignAndInvestment(state.items, betSlipItems)
      itemToUpdate.selectedSuperPickOffer =
        itemToUpdate.selectedSuperPickOffer?.specialSeq === payload.specialOffer?.specialSeq
          ? null
          : payload.specialOffer

      if (isSpecialUsed(itemToUpdate, state.items)) {
        state.items.map(item => {
          if (item.id === payload.id) return item
          if (
            item.selectedSuperPickOffer?.tokenId === itemToUpdate.selectedSuperPickOffer?.tokenId
          ) {
            item.selectedSuperPickOffer = null
          }
          return item
        })
      }
    },

    clearAllReceiptsFromItems(state) {
      state.items.forEach(item => {
        item.receipt = undefined
        item.investment = {
          bonusBet: undefined,
          win: { value: 0, isBonusBet: false, isBonusCash: false },
          place: { value: 0, isBonusBet: false, isBonusCash: false },
        }
      })

      state.multiBetError = null
      state.multiReceipt = undefined
      state.multiInvestment = { ...defaultBetSlipBetsState.multiInvestment }
    },

    removeItemsWithReceipt(state) {
      state.items = state.items.filter(item => !item.receipt)

      const hasMultiBeenPlaced = !!state.multiReceipt
      if (hasMultiBeenPlaced) {
        state.items = state.items.filter(item => !item.isInMulti)
        state.multiReceipt = undefined
        state.multiInvestment = { ...defaultBetSlipBetsState.multiInvestment }
      }
    },

    removeItemsWithFatalError(state) {
      state.items = state.items.filter(hasNoFatalErrors)
    },

    updateBetSlipFobRacingItemPrice(
      state,
      action: PayloadAction<{
        propositionId: number
        winPrice: number | null
        placePrice: number | null
      }>
    ) {
      const { propositionId, winPrice: newWinPrice, placePrice: newPlacePrice } = action.payload
      state.items.map(item => {
        if (
          isFobSelection(item.selection) &&
          item.selection.propositionSeq === propositionId.toString()
        ) {
          const hasWinPriceChanged = newWinPrice && newWinPrice !== item.selection.winPrice
          const hasPlacePriceChanged = newPlacePrice && newPlacePrice !== item.selection.placePrice

          if (hasWinPriceChanged) {
            item.selection.winPriceLastSeen = item.selection.winPrice
            item.selection.winPrice = newWinPrice
          }

          if (hasPlacePriceChanged) {
            item.selection.placePriceLastSeen = item.selection.placePrice
            item.selection.placePrice = newPlacePrice
          }

          if (hasWinPriceChanged || hasPlacePriceChanged) {
            item.selection.priceSource = 'push'
          }
        }
      })
    },

    toggleIsItemInMulti(
      state,
      { payload }: PayloadAction<Pick<BetSlipItem, 'id'> & { isInGroup?: boolean }>
    ) {
      const item = state.items.find(item => item.id === payload.id)
      if (!item) return

      const isToggleItemNowInMulti = !item.isInMulti

      if (isToggleItemNowInMulti && payload.isInGroup) {
        const groupKey = getMultiLegEventOrRaceKey(item.selectionDetails)
        const multiItems = getBetsInMulti(state.items)

        multiItems.map(item => {
          if (item.id === payload.id) return
          if (getMultiLegEventOrRaceKey(item.selectionDetails) === groupKey) {
            item.isInMulti = false
          }
        })
      }
      item.isInMulti = isToggleItemNowInMulti

      if (hasTooFewMultiLegs(state.items) || hasTooManyMultiLegs(state.items)) {
        state.multiInvestment = defaultBetSlipBetsState.multiInvestment
      }
    },

    toggleMultiItemBetLegType(state, { payload }: PayloadAction<Pick<BetSlipItem, 'id'>>) {
      const item = state.items.find(item => item.id === payload.id)
      if (!item) return
      item.multiLegBetType = item.multiLegBetType === 'W' ? 'P' : 'W'
    },

    toggleAllUpFormula(
      state,
      { payload }: PayloadAction<Pick<BetSlipItem, 'id'> & { formula: number }>
    ) {
      const selectedBet = state.items.find(item => item.id === payload.id)
      if (!selectedBet || !isAllUpSelection(selectedBet.selection)) return

      selectedBet.selection.formulas.map(formula => {
        if (formula.formula === payload.formula) {
          formula.isSelected = !formula.isSelected
          return formula
        }
        return formula
      })

      if (selectedBet.selection.formulas.every(formula => !formula.isSelected)) {
        selectedBet.selection.formulas[0].isSelected = true
      }
    },

    setSelectedCampaignForItem(
      state,
      {
        payload,
      }: PayloadAction<{
        id: BetSlipItem['id']
        campaign: Campaign | undefined
        bonusBetInvestmentType?: 'Win' | 'Place'
        bonusCashCampaigns?: Campaign[]
      }>
    ) {
      if (payload.campaign?.id === state.multiInvestment.bonusBetId) {
        state.multiInvestment = resetMultiInvestment(state.multiInvestment)
      }

      state.items = updateItemsWithCampaign(
        state.items,
        payload.id,
        payload.campaign,
        payload.bonusBetInvestmentType
      )

      const { betSlipItems, remainingBonusCashCampaigns } = getItemsWithBonusesApplied(
        [...state.items],
        payload.bonusCashCampaigns ?? [],
        state.multiInvestment
      )

      const availableBonusCashCampaign =
        remainingBonusCashCampaigns.find((campaign: Campaign) => campaign.remainingAmount > 0) ??
        null

      state.items = updateItemsWithCampaignAndInvestment(state.items, betSlipItems)

      if (
        availableBonusCashCampaign &&
        (state.multiInvestment.isBonusBet || state.multiInvestment.isBonusCash)
      ) {
        state.multiInvestment = {
          ...state.multiInvestment,
          bonusBetId: state.multiInvestment.value > 0 ? availableBonusCashCampaign.id : null,
        }
      }
    },

    updateItemsWithBonusCashChanges(
      state,
      {
        payload,
      }: PayloadAction<{
        bonusCashCampaigns?: Campaign[]
      }>
    ) {
      const { betSlipItems, betSlipMultiInvestment } = getItemsWithBonusesApplied(
        [...state.items],
        payload.bonusCashCampaigns ?? [],
        state.multiInvestment,
        state.multiReceipt
      )

      state.items = updateItemsWithCampaignAndInvestment(state.items, betSlipItems)
      state.multiInvestment = {
        ...state.multiInvestment,
        isBonusCash: betSlipMultiInvestment.isBonusCash,
        bonusBetId: betSlipMultiInvestment.bonusBetId,
      }
    },

    setSelectedCampaignForMulti(
      state,
      {
        payload,
      }: PayloadAction<{
        campaign: Campaign | undefined
      }>
    ) {
      if (!payload.campaign) return

      const { campaign } = payload

      state.items = state.items.map(item => {
        if (campaign.rewardType === 'BonusBetReward' && item.campaign?.id === campaign.id) {
          return {
            ...item,
            investment: {
              win: { value: 0, isBonusBet: false, isBonusCash: false },
              place: { value: 0, isBonusBet: false, isBonusCash: false },
              bonusBet: undefined,
            },
            campaign: undefined,
          }
        }
        return item
      })

      if (campaign.id === state.multiInvestment.bonusBetId) {
        state.multiInvestment = resetMultiInvestment(state.multiInvestment)
      } else {
        if (campaign.rewardType === 'BonusBetReward') {
          state.multiInvestment = {
            ...state.multiInvestment,
            value: campaign.remainingAmount,
            bonusBetId: campaign.id,
            isBonusBet: true,
            isBonusCash: false,
            f1: 0,
            f2: 0,
            f3: 0,
            f4: 0,
            f5: 0,
          }
        } else if (campaign.rewardType === 'BonusCashReward') {
          state.multiInvestment = {
            ...state.multiInvestment,
            bonusBetId: campaign.id,
            isBonusCash: true,
            isBonusBet: false,
          }
        }
      }
    },

    toggleIsEachWay(
      state,
      { payload }: PayloadAction<Pick<BetSlipItem, 'id'> & { isEachWay: boolean }>
    ) {
      const item = state.items.find(item => item.id === payload.id)
      if (!item) return
      item.isEachWay = payload.isEachWay
      item.investment.place.value =
        payload.isEachWay && item.investment.win.value ? item.investment.win.value : 0
    },

    resetCampaignForItem(
      state,
      {
        payload,
      }: PayloadAction<{
        id: BetSlipItem['id']
        bonusCashCampaigns?: Campaign[]
      }>
    ) {
      const itemToUpdate = state.items.find(item => item.id === payload.id)
      const { betSlipItems } = getItemsWithBonusesApplied(
        [...state.items],
        payload.bonusCashCampaigns ?? [],
        state.multiInvestment,
        state.multiReceipt
      )

      if (itemToUpdate) {
        if (itemToUpdate?.campaign?.id) {
          itemToUpdate.campaign = undefined
          itemToUpdate.investment.win.value = 0
          itemToUpdate.investment.place.value = 0
          itemToUpdate.investment.win.isBonusBet = false
          itemToUpdate.investment.place.isBonusBet = false
          itemToUpdate.investment.win.isBonusCash = false
          itemToUpdate.investment.place.isBonusCash = false
          itemToUpdate.investment.bonusBet = undefined
        }

        state.items = updateItemsWithCampaignAndInvestment(state.items, betSlipItems)
      }
    },
  },

  extraReducers: builder => {
    // =======
    // Refresh
    // =======
    builder.addCase(refreshAllBets.fulfilled, (state, action) => {
      const data = action.payload
      const multiBetResponse = getMultiBetResponse(data)
      const newItems = state.items.map(item => {
        return mapResponse(item, data, multiBetResponse, true)
      })

      const multiBetError = getMultiBetErrorDescription(multiBetResponse)
      state.items = newItems
      state.multiBetError = multiBetError
    })

    // =======
    // Propose
    // =======
    builder.addCase(proposeAllBets.fulfilled, (state, action) => {
      const data = action.payload
      const multiBetResponse = getMultiBetResponse(data)
      const newItems = state.items.map(item => mapResponse(item, data, multiBetResponse))
      const multiBetError = getMultiBetErrorDescription(multiBetResponse)

      state.items = newItems
      state.multiBetError = multiBetError
      state.multiInvestment =
        multiBetError && multiBetError.betErrorType === 'Unspecified'
          ? defaultBetSlipBetsState.multiInvestment
          : state.multiInvestment
    })

    // =======
    // Confirm
    // =======
    builder.addCase(confirmAllBets.fulfilled, (state, action) => {
      const data = action.payload
      const multiBetResponse = getMultiBetResponse(data)
      const newItems = state.items.map(item => mapResponse(item, data, multiBetResponse))
      const multiBetError = getMultiBetErrorDescription(multiBetResponse)

      state.items = newItems
      state.multiBetError = multiBetError
      state.multiInvestment =
        multiBetError && multiBetError.betErrorType === 'Unspecified'
          ? defaultBetSlipBetsState.multiInvestment
          : state.multiInvestment

      let newMultiReceipt: FobBetReceiptResponse | undefined = undefined
      if (multiBetResponse && multiBetResponse.success) {
        newMultiReceipt = multiBetResponse.receipt as FobBetReceiptResponse
      }

      state.multiReceipt = newMultiReceipt
    })
  },
})

// =============
// Slice Exports
// =============

export const {
  addItemsToBetSlip,
  clearAllBetSlipItems,
  removeItemFromBetSlipById,
  setInvestmentAmount,
  setSelectedSpecialOffer,
  clearAllReceiptsFromItems,
  removeItemsWithReceipt,
  updateBetSlipFobRacingItemPrice,
  removeItemsWithFatalError,
  toggleIsItemInMulti,
  toggleMultiItemBetLegType,
  toggleAllUpFormula,
  setSelectedCampaignForItem,
  setSelectedCampaignForMulti,
  updateItemsWithBonusCashChanges,
  toggleIsEachWay,
  resetCampaignForItem,
} = betsSlice.actions

const allowPersistForKeys: (keyof BetSlipBetsState)[] = ['items']

export default persistReducer(
  {
    version: 1,
    key: 'betslip/bets',
    storage,
    // debug: !PRODUCTION,
    whitelist: allowPersistForKeys,
  },
  betsSlice.reducer
)

// =====
// Types
// =====

export interface BetSlipBetsState {
  items: BetSlipItem[]

  isMultiExpanded: boolean
  isSingleExpanded: boolean
  multiBetError: { betErrorType: BetErrorType; errorMessage: string } | null
  multiInvestment: MultiInvestment
  multiReceipt?: FobBetReceiptResponse
}

type SetInvestmentAmountArgs = PayloadAction<{
  itemId: BetSlipItem['id']
  investmentType: InvestmentType
  value: string
}>
